import { Link, createFileRoute } from '@tanstack/react-router';
import { Image } from '@unpic/react';
import { useEffect } from 'react';
import Icon from '../../components/icon';
import { getProducts } from '../../server/catalog.functions';
import { ProductSortKeys } from '../../types/types';
import { seo } from '../../utils/seo';
import { slugToTitle } from '../../utils/slug';
import { organizationJsonLd, webSiteJsonLd } from '../../utils/structured-data';

export const Route = createFileRoute('/_layout/')({
  loader: async () => {
    const products = await getProducts({
      data: { first: 4, sortKey: ProductSortKeys.BestSelling }
    });
    return { featured: products.nodes };
  },
  head: () => ({
    meta: seo({ title: 'Az inspiráló design karkötő' }),
    scripts: [organizationJsonLd(), webSiteJsonLd()]
  }),
  component: IndexRoute
});

const topFeaturedProductHandle = 'break-your-limits';
const featuredProductsGradientMap: Record<string, string> = {
  'break-your-limits':
    'linear-gradient(45deg, hsl(206, 100%, 50%), hsl(206, 100%, 90%))',
  'strength-and-beauty':
    'linear-gradient(45deg, hsl(332, 70%, 30%), hsl(332, 100%, 85%))',
  focus: 'linear-gradient(45deg, hsl(0, 0%, 75%), hsl(0, 0%, 95%))',
  'stay-positive':
    'linear-gradient(45deg, hsl(37, 100%, 55%), hsl(37, 100%, 95%))'
};

function IndexRoute() {
  const { featured } = Route.useLoaderData();
  const linkHandle = 'karkotok'; // The collection's slug in Shopify

  const featuredProducts = featured
    .toSorted((a, b) => {
      // Show top always first in the featured products section
      if (a.handle === topFeaturedProductHandle) return -1;
      if (b.handle === topFeaturedProductHandle) return 1;
      return 0;
    })
    .map(({ title, handle, featuredImage }, index) => (
      <Link
        className="grid-item"
        to="/product/$handle"
        params={{ handle }}
        key={index}
        style={{
          backgroundImage: featuredProductsGradientMap[handle] ?? 'none'
        }}
      >
        <Image
          src={featuredImage!.url}
          layout="constrained"
          width={592}
          height={592}
          priority // Eagerly load the featured products images for better performance and SEO
          alt={`${title} karkötő`}
        />
      </Link>
    ));

  const landingImages = [
    {
      handle: 'free-spirit',
      src: 'https://cdn.shopify.com/s/files/1/0418/2720/5275/files/free-spirit-landing.jpg?v=1781419434',
      width: 592,
      height: 592
    },
    {
      handle: 'stay-positive',
      src: 'https://cdn.shopify.com/s/files/1/0418/2720/5275/files/stay-positive-landing.jpg?v=1781419435',
      width: 288,
      height: 288
    },
    {
      handle: 'keep-the-balance',
      src: 'https://cdn.shopify.com/s/files/1/0418/2720/5275/files/keep-the-balance-landing.jpg?v=1781419434',
      width: 288,
      height: 288
    },
    {
      handle: 'reach-for-the-stars',
      src: 'https://cdn.shopify.com/s/files/1/0418/2720/5275/files/reach-for-the-stars-landing.jpg?v=1781419434',
      width: 592,
      height: 288
    },
    {
      handle: 'make-today-amazing',
      src: 'https://cdn.shopify.com/s/files/1/0418/2720/5275/files/make-today-amazing-landing.jpg?v=1781419433',
      width: 592,
      height: 592
    },
    {
      handle: 'give-it-all',
      src: 'https://cdn.shopify.com/s/files/1/0418/2720/5275/files/give-it-all-landing.jpg?v=1781419434',
      width: 288,
      height: 288
    },
    {
      handle: 'you-are-enough',
      src: 'https://cdn.shopify.com/s/files/1/0418/2720/5275/files/you-are-enough-landing.jpg?v=1781419436',
      width: 288,
      height: 288
    },
    {
      handle: 'grow-everyday',
      src: 'https://cdn.shopify.com/s/files/1/0418/2720/5275/files/grow-everyday-landing.jpg?v=1781419433',
      width: 592,
      height: 288
    }
  ].map(({ handle, src, width, height }, index) => (
    <Link
      key={index}
      className={`landing-image landing-image${index + 1}`}
      to="/product/$handle"
      params={{ handle }}
    >
      <Image
        src={src}
        layout="constrained"
        width={width}
        height={height}
        alt={`${slugToTitle(handle)} motivációs karkötő`}
      />
    </Link>
  ));

  useEffect(() => {
    // Initializing the Stamped reviews widget on the homepage. Needs to do this here because the widget needs to be initialized after the script is loaded, and we are loading the script in the root route. We also need to do this in a useEffect to make sure it runs only on the client side.
    window.StampedFn?.init({
      apiKey: import.meta.env.VITE_STAMPED_API_KEY,
      storeUrl: import.meta.env.VITE_SHOPIFY_STORE_DOMAIN
    });
  }, []);

  return (
    <>
      <div className="intro container-fluid">
        <div className="intro-headings">
          <svg
            className="logo"
            viewBox="0 0 816 147"
            width="266"
            height="48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M673.386 146.99c31.873 0 50.266-14.039 50.266-33.851 0-41.938-64.33-24.323-64.33-42.533 0-5.554 5.942-9.918 19.808-9.918 9.706 0 20.196 1.984 30.678 7.935l10.285-21.974c-10.087-5.932-26.305-9.293-40.963-9.293-31.082 0-49.277 14.236-49.277 34.44 0 42.534 64.331 24.713 64.331 42.137 0 5.954-5.348 9.725-19.61 9.725-13.073 0-27.514-4.168-37.008-10.321l-10.286 22.185c9.891 6.525 28.086 11.468 46.106 11.468zm133.8-28.48c-3.365 2.574-7.918 3.96-12.471 3.96-8.313 0-13.262-4.95-13.262-14.059V65.522h26.524V38.939h-26.524V6.873h-30.854v32.066H734.17v26.583h16.429v43.285c0 25.33 14.636 38.183 39.762 38.183 9.501 0 18.804-2.175 25.138-6.723l-8.313-21.757zM59.966 6.87H.002V146.4h32.042v-39.203h27.922c37.02 0 60.161-19.182 60.161-50.067 0-31.082-23.141-50.264-60.161-50.264zm74.438 139.53h30.854V38.93h-30.854V146.4zm447.74 0h30.854V38.93h-30.854V146.4zM531.25 52.982v-14.05h-29.47v107.47h30.854V95.136c0-20.399 11.283-29.702 28.306-29.702 2.375 0 4.355.198 6.928.395V37.351c-16.429 0-29.097 5.343-36.618 15.633zM295.7 146.4l-41.163-55.422 39.58-52.046h-32.851l-23.155 31.07-22.56-31.07h-34.632l39.579 52.838-40.965 54.63h33.643l24.144-33.653 23.352 33.653H295.7zM463.95 38.93l-25.927 72.051L412.69 38.93h-26.124l-26.125 72.051-25.135-72.051h-29.093l38.395 107.47h29.687l24.739-67.088 23.948 67.088h29.687l38.594-107.47H463.95zM58.18 81.092H32.041V32.967H58.18c19.605 0 29.507 8.912 29.507 24.161 0 15.052-9.902 23.964-29.507 23.964zM149.887-.004c8.546 0 15.485 6.939 15.485 15.486 0 8.546-6.939 15.485-15.485 15.485-8.547 0-15.486-6.939-15.486-15.485 0-8.547 6.939-15.486 15.486-15.486zm447.62 0c8.547 0 15.485 6.939 15.485 15.486 0 8.546-6.938 15.485-15.485 15.485s-15.486-6.939-15.486-15.485c0-8.547 6.939-15.486 15.486-15.486z"
              fill="currentColor"
            />
          </svg>
          <h1 className="brand-slogan">Inspiráció, ami mindig Veled van</h1>
        </div>
        <Link
          className="products-link"
          to="/$handle"
          params={{ handle: linkHandle }}
        >
          Nézd meg a karkötőket
          <Icon className="products-link-icon" name="arrow-right" size={20} />
        </Link>
      </div>

      <div className="featured-products container-fluid">
        <div className="featured-products-grid grid centered">
          {featuredProducts}
          <Link
            className="grid-item more-products-link"
            to="/$handle"
            params={{ handle: linkHandle }}
          >
            Összes karkötő
            <Icon className="products-link-icon" name="arrow-right" size={20} />
          </Link>
        </div>
      </div>

      <div className="what-is container-fluid" data-aos="fade-up">
        <div className="centered">
          <h2 className="text-center">
            Fedezd fel a motiváció erejét stílusos, kétoldalú karkötők
            formájában!
            <br />
            Légy önmagad legjobb verziója &ndash; minden nap.
          </h2>
        </div>
      </div>

      <div className="landing-images container-fluid">
        <div className="landing-images-grid grid centered">{landingImages}</div>
      </div>

      <div className="features container-fluid" data-aos="fade-up">
        <div className="features-grid grid centered">
          <div className="feature feature-motivating text-center">
            <Icon
              className="feature-icon"
              name="sun"
              size={32}
              strokeWidth={1.5}
            />
            <h3 className="feature-name">Motiváló üzenetek</h3>
            <p className="feature-text">
              Hozd ki magadból a maximumot pozitív, inspiráló szövegeinkkel.
            </p>
          </div>
          <div className="feature feature-lightweight text-center">
            <Icon
              className="feature-icon"
              name="feather"
              size={32}
              strokeWidth={1.5}
            />
            <h3 className="feature-name">Ultrakényelmes viselet</h3>
            <p className="feature-text">
              A puha, rugalmas textil anyag egész nap kényelmes érzetet nyújt.
            </p>
          </div>
          <div className="feature feature-double-sided text-center">
            <Icon
              className="feature-icon"
              name="repeat"
              size={32}
              strokeWidth={1.5}
            />
            <h3 className="feature-name">Kifordítható design</h3>
            <p className="feature-text">
              Egy karkötő, két stílus &ndash; variáld hangulatod szerint!
            </p>
          </div>
          <div className="feature feature-patterns text-center">
            <Icon
              className="feature-icon"
              name="star"
              size={32}
              strokeWidth={1.5}
            />
            <h3 className="feature-name">Egyedi és színes</h3>
            <p className="feature-text">
              Válogass változatos mintáink közül, hogy megtaláld a Hozzád
              legjobban illőt.
            </p>
          </div>
        </div>
      </div>

      <div className="check-products container-fluid" data-aos="fade-right">
        <div className="centered">
          <Link
            className="products-link"
            to="/$handle"
            params={{ handle: linkHandle }}
          >
            Nézd meg a karkötőket
            <Icon className="products-link-icon" name="arrow-right" size={20} />
          </Link>
        </div>
      </div>

      <div className="reviews-wrapper container-fluid">
        <div
          id="stamped-reviews-widget"
          data-widget-type="carousel"
          data-title="Vásárlói vélemények"
          data-min-rating="4"
          data-random="true"
        />
      </div>

      <div className="social container-fluid" data-aos="fade-up">
        <div className="centered">
          <h3 className="text-center">
            Kövess minket az{' '}
            <a
              href="https://www.instagram.com/pixwrist"
              target="_blank"
              rel="noopener"
            >
              Instagramon
            </a>{' '}
            és a{' '}
            <a
              href="https://www.tiktok.com/@pixwrist"
              target="_blank"
              rel="noopener"
            >
              TikTokon
            </a>
            , és mutasd meg nekünk, hogyan viseled Pixwrist karkötőidet!
          </h3>
        </div>
      </div>
    </>
  );
}
